import { appConfig } from "@common-config/app.config";
import { WhatsappId } from "@bt-common/interfaces/whatsapp-types";
import axios, { AxiosResponse } from "axios";
import moment from "moment";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import Resizer from 'react-image-file-resizer';
import { isExtensionContextOrWhatsappView } from "./detect-context";
import { isMessageIsWithinSendingLimits } from '@/bt-common/services/utilsCommon';
import { UserMessage } from '@/bt-common/schemas/bt/user-message';
import { isExtensionContext } from "./detect-context";


export function loadScript(src: string): Promise<Event> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script ${src}`));
    document.head.appendChild(script);
  });
}

export function safeCreateObjectURL(blob: Blob): string {
  try {
    return URL.createObjectURL(blob);
  } catch (e) {
    return "";
  }
}

export function getContext(): string {
  let context = "unkown";
  if (typeof chrome !== 'undefined' && chrome.runtime !== undefined && chrome.runtime.id) {
    if (typeof window === "undefined") {
      context = "background script";
    } else {
      context = "content script";
    }
  } else {
    context = "page";
  }
  return context;
}


export const useConstructor = (callBack = () => { }) => {
  const hasBeenCalled = useRef(false);
  if (hasBeenCalled.current) return;
  callBack();
  hasBeenCalled.current = true;
};

export function modifyPlanName(planName: unknown) {

  // if planName is not string, return it as is
  if (typeof planName !== 'string') {
    return "";
  }

  let [firstPlanNameToken] = planName.split('-');

  const replacements: Record<string, string> = {
    'Scheduler': 'Basic',
    'Manager': 'StandardNew',
    'Campaigner': 'Pro'
  };

  // Replace the matching keys in the planName with their corresponding values in replacements
  for (const key in replacements) {
    if (firstPlanNameToken.includes(key)) {
      firstPlanNameToken = firstPlanNameToken.replace(new RegExp(`\\b${key}\\b`, 'gi'), replacements[key]);
    }
  }
  // Check if the planName string contains  ' 1 ' If true, replace 'seats' with 'seat' in the planName.
  if (planName.includes(' 1 ')) {
    planName = planName.replace('seats', 'seat');
  }

  // Return the modified planName
  return firstPlanNameToken;
}



export function escapeHtml(unsafe: string | undefined | null) {
  if (!unsafe) return "";

  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}


export function hexToRGB(hex: string, alpha: string) {

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return `rgb(${r}, ${g}, ${b})`;
}

export function getDisplayNameFromFirstLastAndEmail(firstName?: string, lastName?: string, email?: string): string {

  let displayName = '';

  if (firstName) {
    displayName = firstName;
  }

  if (lastName) {
    displayName += ' ' + lastName;
  }

  if (displayName === '' && email) {
    displayName += email
  }

  return displayName.trim()

}

export const insertTextAtCursor = (
  ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement>,
  text: string,
  preserveFormat: boolean = false
) => {
  const primitivePlaceholderFormat = preserveFormat ? text : text.replace(/\s+/g, '').toLowerCase();
  const inputElement = ref?.current?.querySelector("textarea") || ref?.current;

  if (!inputElement) return null;

  let msg: string;
  let textBeforeCursorPosition: string;

  const cursorPosition = inputElement.selectionStart ?? 0;
  textBeforeCursorPosition = inputElement?.value.substring(
    0,
    cursorPosition
  );
  const textAfterCursorPosition = inputElement?.value.substring(
    cursorPosition,
    inputElement.value.length
  );

  msg = `${textBeforeCursorPosition ?? ""}${primitivePlaceholderFormat}${textAfterCursorPosition ?? ""}`;

  inputElement.value = msg;

  const newCursorPosition = textBeforeCursorPosition.length + primitivePlaceholderFormat.length;

  inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
  inputElement.focus();

  return inputElement.value;
};

export const messageFormatBtn = (
  ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement>,
  wrap: "*" | "_" | "~"
) => {
  const inputElement: any = ref.current;

  if (inputElement && (inputElement instanceof HTMLTextAreaElement || inputElement instanceof HTMLInputElement)) {
    let res;
    if (inputElement.selectionStart !== inputElement.selectionEnd) {
      const msg = inputElement.value;
      const selStart = inputElement.selectionStart ?? 0;
      const selEnd = inputElement.selectionEnd ?? 0;

      res = [
        msg.slice(0, selStart),
        wrap,
        msg.slice(selStart, selEnd),
        wrap,
        msg.slice(selEnd),
      ].join("");

      // Set the new value to the input element
      inputElement.value = res;

      // Set the cursor position after the formatting
      const cursorPosition = selStart + wrap.length;
      inputElement.setSelectionRange(cursorPosition, cursorPosition);
    }

    inputElement.focus();

    return res;
  } else if (inputElement.children[0] instanceof HTMLTextAreaElement) {
    let res;
    if (inputElement.children[0].selectionStart !== inputElement.children[0].selectionEnd) {
      const msg = inputElement.children[0].value;

      res = [
        msg.slice(0, inputElement.children[0].selectionStart),
        wrap,
        msg.slice(inputElement.children[0].selectionStart, inputElement.children[0].selectionEnd),
        wrap,
        msg.slice(inputElement.children[0].selectionEnd),
      ].join("");

      // Set the new value to the input element
      inputElement.children[0].value = res;

      // Set the cursor position after the formatting
      const cursorPosition = inputElement.children[0].selectionStart + wrap.length;
      inputElement.children[0].setSelectionRange(cursorPosition, cursorPosition);
    }

    inputElement.children[0].focus();

    return res;
  } else {
    console.error("Ref did not point to a textarea/input element.");
    return null;
  }
};


export async function fileToBase64(file: File | Blob): Promise<string> {
  let base64Output = '';
  try {
    const resizedImage = await new Promise<string>((resolve, reject) => {
      Resizer.imageFileResizer(file, 162, 162, 'JPEG', 100, 0, (image) => {
        if (typeof image === 'string') resolve(image);
        else reject(new Error('Resizer returned non-string output for base64 mode'));
      }, 'base64');
    })
    const [_, image] = resizedImage.split(',')
    base64Output = image;
  } catch (e) {
    console.warn('Unable to resize image')
  }
  return base64Output;
}


export function isValidEmail(email: string | undefined) {
  if (!email) return false;
  // Escape special characters in the email before using it in regex
  const regex = /^[_a-z0-9-]+(\.[_a-z0-9-]+)*(\+[a-z0-9-]+)?@[a-z0-9-]+(\.[a-z0-9-]+)*$/i;
  return regex.test(email);
}

export const useFocus = (): [MutableRefObject<HTMLElement | null>, () => void] => {
  const htmlElRef = useRef<HTMLElement | null>(null)
  const setFocus = () => { htmlElRef.current && htmlElRef.current.focus() }

  return [htmlElRef, setFocus]
}

export interface OpenWindowOptions {
  popup?: boolean;
  eventName?: string;
}

export function openWindow(url: string, title: string, options?: OpenWindowOptions): Promise<void> {
  const { popup, eventName } = options ?? {};
  return new Promise(async (resolve, reject) => {
    const ref = window.open(url, title, `target=_blank,${popup ? 'scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=1000,height=1000,left=100,top=100' : ''}`)

    let result: unknown;
    const timer = setInterval(() => {
      if (ref && ref.closed) {
        clearInterval(timer);
        resolve();
      }
    }, 500);

    if (eventName) {
      result = await new Promise(resolve => window.addEventListener(eventName, resolve));
      console.log(result);
    }
  });
}

export const varNameFromObject = (varObject: any) => {
  const [varName] = Object.keys(varObject);
  return varName;
}

export const openPaymentSite = async (accessToken: string, openInNewTab: boolean = false) => {
  const url = `${appConfig.PRICING_PAGE}/account-subscription?v=1&accessToken=${accessToken}`;
  if (typeof chrome !== 'undefined' && chrome.tabs) {
    // We are on the extension popup
    chrome.tabs.create({
      url
    });
  } else if (typeof chrome !== 'undefined' && chrome.extension) {
    window.open(url, "_blank");

  } else {
    // We are on the website
    if (openInNewTab) {
      window.open(url, "_blank");
    } else {
      window.location.href = url;
    }
  }
}

export const openCreditsPage = async (accessToken: string, autoOpenModal: boolean = true) => {
  const params = new URLSearchParams();
  params.set('accessToken', accessToken);
  if (autoOpenModal) {
    params.set('openAddCredits', 'true');
  }
  
  const url = `${appConfig.PRICING_PAGE}/credits-usage?${params.toString()}`;
  
  if (typeof chrome !== 'undefined' && chrome.tabs) {
    // We are on the extension popup
    chrome.tabs.create({
      url
    });
  } else if (typeof chrome !== 'undefined' && chrome.extension) {
    window.open(url, "_blank");
  } else {
    // We are on the website
    window.location.href = url;
  }
}

export const serializedIdToObject = (serializedId: string): WhatsappId => {
  const [user, server] = serializedId.split('@');
  return {
    user,
    server,
    _serialized: serializedId,
  }
}

export function getFromExtensionLocalStorage(key: string) {
  return new Promise((resolve, reject) => {
    if (typeof chrome === 'undefined' || !chrome.storage) {
      reject(new Error('Chrome extension storage not available'));
      return;
    }
    
    chrome.storage.local.get(key, (result) => {
      if (chrome.runtime.lastError) {
        reject(chrome.runtime.lastError);
      } else {
        resolve(result[key]);
      }
    });
  });
}

export async function isLoggingEnabled() {

  let loggingEnabledByURL = false;
  let loggingEnabledByStoarge = false;

  if (isExtensionContextOrWhatsappView()) {
    loggingEnabledByStoarge = Boolean(await getFromExtensionLocalStorage('loggingEnabled'));
  }
  else {
    loggingEnabledByStoarge = Boolean(localStorage.getItem('loggingEnabled'));
    const params = new URLSearchParams(window.location.search);
    loggingEnabledByURL = params.get('loggingEnabled') === 'true';
  }

  return loggingEnabledByStoarge || loggingEnabledByURL;

}


// need to find the right place to call this function
export async function disableProdLogging() {

  //console.log(appConfig);
  //console.log("appConfig.ENV", appConfig.ENV);

  switch (appConfig.ENV) {
    case "production":
    case "prod":
    case "development":
    case "dev": {
      const loggingEnabled = await isLoggingEnabled();
      console.log("loggingEnabled", loggingEnabled);
      if (!loggingEnabled) {
        console.log = function () { };
      }
      break;
    }
  }



}



export async function blobToBase64Async(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = function () {
      resolve(reader.result as string);
    };
  });
}

// wa-scheduler#121: uploadFilePut lives in its own module now — it grew a stall
// watchdog and a retry policy that need unit tests, and pulling them in through
// this barrel would drag moment/react/Resizer/appConfig into every such test.
// Re-exported here so the existing `@common-services/utils` importers are unaffected.
export { UploadStalledError, uploadFilePut } from "./upload-file-put";
export type { UploadFilePutOptions } from "./upload-file-put";

export async function getBase64ImageFromUrl(imageUrl: string): Promise<string> {
  let res = '';
  try {
    const response: AxiosResponse<Blob> = await axios.get(
      imageUrl,
      { responseType: "blob" }
    );
    const blob = response.data;

    const base64data = await blobToBase64Async(blob);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, thumbnail] = base64data.split(',');
    res = thumbnail;
  } catch (e) {
    //
  }

  return res;
}

export function getUrls(str: string): Set<string> | undefined {

  const regexp = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,500}\.[a-zA-Z0-9()]{1,500}([-a-zA-Z0-9@:%_\+~#?!&\/\/\u00A0-\uFFFF]*[a-zA-Z0-9@:%_\+~#=\/\u00A0-\uFFFF])*(\.[a-zA-Z0-9]{1,500})?(\?[a-zA-Z0-9@:%_\+~#&=\/\u00A0-\uFFFF]*)?/gi;

  const emptySet = new Set<string>();
  const bracketsRegexp = /[()]/g;
  if (typeof str !== "string") {
    return emptySet;
  }

  if (str) {
    let urls = str.match(regexp);
    if (urls) {
      return new Set(urls.map((item) => item.replace(bracketsRegexp, "")));
    } else {
      return emptySet;
    }
  } else {
    return emptySet;
  }
}


export const extractTimeFromISOString = (isoString: string) => {
  const date = new Date(isoString);
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}



export const changeTimeInISOString = (isoString: string, newTime: string) => {
  // Validate the newTime format
  const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timePattern.test(newTime)) {
    throw new Error('Invalid time format. Please provide time in "hh:mm" format.');
  }

  // Parse the ISO string as local time
  const localDateTime = moment(isoString).format('YYYY-MM-DD') + 'T' + newTime;

  // Convert the local time to UTC
  const utcTime = moment(localDateTime).utc().format();

  return utcTime;
}


export const getCampaignDueTime = (scheduleDueDate?: string): moment.Moment => {
  const dueTime: moment.Moment = moment.utc(scheduleDueDate);
  const { hours, minutes } = moment(scheduleDueDate).startOf('minute').toObject();
  dueTime.hours(hours);
  dueTime.minutes(minutes);
  return dueTime;
};


export const isScheduleValid = (scheduleDueDate?: string) => {
  const inputTime = moment.utc(scheduleDueDate);
  const currentTime = moment.utc();
  return inputTime.isAfter(currentTime);
};

interface UseCountUpOptions {
  isCounting: boolean;
  start: number;
  end: number;
  minDuration: number;
  maxDuration: number;
}

export const useCountUp = ({ isCounting, start, end, minDuration, maxDuration }: UseCountUpOptions) => {
  const [value, setValue] = useState<number>(start);
  const requestRef = useRef<number | null>(null);
  const isResetting = useRef(false); // Track if we are in the process of resetting

  const resetAndAnimate = useCallback(() => {
    const duration = minDuration + Math.random() * (maxDuration - minDuration); // Random duration within specified range
    let startTime: number | undefined;

    const animate = (time: number) => {
      if (!startTime) {
        startTime = time;
      }
      const timeElapsed = time - startTime;
      const progress = timeElapsed / duration;
      const currentValue = Math.min(start + (end - start) * progress, end);

      if (currentValue >= end && !isResetting.current) {
        isResetting.current = true;
        setValue(start); // Instantly reset to start value
        requestAnimationFrame(() => {
          isResetting.current = false;
          resetAndAnimate(); // Start again
        });
      } else if (!isResetting.current) {
        setValue(currentValue);
        requestRef.current = requestAnimationFrame(animate);
      }
    };

    requestRef.current = requestAnimationFrame(animate);
  }, [start, end, minDuration, maxDuration]);

  useEffect(() => {
    if (isCounting) {
      resetAndAnimate();
    }

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [isCounting, resetAndAnimate]);

  return value;
};

export const getMessageFromInputField = () => {
  const paragraphs = document.querySelector("footer [contenteditable]")?.querySelectorAll('p');
  if (!paragraphs) return '';
  const message = Array.from(paragraphs)
    .map(paragraph => paragraph.textContent).join("\r\n");
  return message;
}



export const generateNewAudienceName = () => {
  // ${t('importExcelToBlueticks.newAudience')}
  return `New Audience ${moment().format('YYYY-MM-DD HH:mm')}`;
}


interface HandleSlot { id?: string }
interface HandlesShape { inputs?: HandleSlot[]; outputs?: HandleSlot[] }
export const getHandleId = (handles: HandlesShape | undefined | null, type: 'target' | 'source', index: number) => {
  if (type === 'target') {
    return handles?.inputs?.[index]?.id;
  } else {
    return handles?.outputs?.[index]?.id;
  }
}

export const getChatbotTempId = () => {
  return 'BotElementId' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

export const handleMentionsFormatInMessage = (message: string) => {
  if (!message) {
    return '';
  }
  // Regex pattern to match @[displayName](contactId)
  const pattern = /@\[(.*?)\]\(.*?\)/g;

  // Replace the matches with @displayName
  const result = message?.replace(pattern, '@$1');

  return result;
};

// NOTE: isMessageOverDue was removed - use isMessageOverdue from '@/bt-common/services/utilsCommon' instead
// The utilsCommon version properly handles missing dueDate and respects retry limits via getUpperLimit()

export const checkBotSettingsValidation = (botSettings: any) => {
  return botSettings?.fallbackMessage?.length > 1024 ||
    botSettings?.chatbotTimerCountdown <= 1 ||
    botSettings?.chatbotTimerCountdown > 1440 ||
    botSettings?.chatbotTimerEndMessage?.length > 1024 ||
    botSettings?.chatbotTimerMessageBeforeEnd <= 0 ||
    botSettings?.chatbotTimerMessageBeforeEnd >= botSettings?.chatbotTimerCountdown;
};
export const useSafeLocation = (): { search: string; pathname: string } => {
  if (typeof window !== 'undefined' && window.location) {
    return { search: window.location.search, pathname: window.location.pathname };
  }
  return { search: '', pathname: '' };
}


export const getTargetHandleStyle = (nodeColor: string) => {
  return {
    backgroundColor: nodeColor,
    width: 18,
    height: 18,
    left: -9
  }
}

export const getSourceHandleStyle = (nodeColor: string) => {
  return {
    backgroundColor: nodeColor,
    width: 18,
    height: 18,
    right: -9
  }
}

/**
 * Sets or removes dark/light mode classes on the body element.
 * Tailwind CSS is configured to use .dark selector (not body.dark) for Shadow DOM compatibility.
 * The .dark selector works in both regular DOM (when on body) and Shadow DOM (when on wrapper div).
 * 
 * @param isDarkMode - If true, adds 'dark' and removes 'light'; if false, does the opposite
 */
export function setDarkModeClasses(isDarkMode: boolean): void {
  const bodyEl = document.body;
  
  if (isDarkMode) {
    bodyEl.classList.add('dark');
    bodyEl.classList.remove('light');
  } else {
    bodyEl.classList.remove('dark');
    bodyEl.classList.add('light');
  }
}
